import { useState, JSX } from 'react';
import {
    useRecordContext
} from 'react-admin';
import { useFormContext } from "react-hook-form"
import Editor from '@monaco-editor/react';
import DOMPurify from 'dompurify';
import {
    Grid,
    Box,
    Drawer
} from '@mui/material';
import { hostUrl } from "../utils/url-manager";

export const NokEmailSignatureCodeEditor: () => JSX.Element = (): JSX.Element => {
    const record = useRecordContext();
    const { setValue } = useFormContext();
    const [html, setHtml] = useState<string>(record?.html ?? '');
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [previewHtml, setPreviewHtml] = useState<string>('');

    function setHtmlContent(value: string, event): void {
        let cleanHtml: string  = DOMPurify.sanitize(
            value,
            {}
        );
        setHtml(cleanHtml);
        setValue('html', cleanHtml, { shouldDirty: true } );
    }

    function handleShowPreviewClick(): void {
        const prevHtml: string = parseHtmlVariables()
        setPreviewHtml(prevHtml)
        setShowPreview(!showPreview);
    }

    function parseHtmlVariables(): string {
        let currentHtml: string = html;
        const placeholderImages: string[] = [
            hostUrl + 'images/admin/nok/email/signature/defaults/female-placeholder.png',
            hostUrl + 'images/admin/nok/email/signature/defaults/male-placeholder.png'
        ];
        const placeholderNames: string[] = [
            'Jane Doe',
            'John Doe',
        ];
        const placeholderEmails: string[] = [
            'jane.doe@noknok.ai',
            'john.doe@noknok.ai'
        ];
        // debugger;
        const randomNum: number = Math.floor(Math.random() * placeholderImages.length);
        const tempName: string = placeholderNames[randomNum];
        const tempImage: string = placeholderImages[randomNum];
        const placeholderEmail: string = placeholderEmails[randomNum];

        if (currentHtml.includes('{{persona_name}}')) {
            const nameString: string = `<span>${tempName}</span>`
            currentHtml = currentHtml.replace('{{persona_name}}', nameString );
        }
        if (currentHtml.includes('{{persona_image}}')) {
            const imgString: string = `<img src="${tempImage}" alt="${tempName}" style="width: 100%;height:auto;max-width: 100%;" />`;
            currentHtml = currentHtml.replace('{{persona_image}}', imgString );
        }
        if (currentHtml.includes('{{persona_email}}')) {
            const emailString: string = `<a href="mailto:${placeholderEmail}" 
                                    style="color:inherit;text-decoration:inherit;font-size:inherit;font-weight:inherit">
                                    ${placeholderEmail}</a>`
            currentHtml = currentHtml.replace('{{persona_email}}', emailString );
        }
        if (currentHtml.includes('{{persona_title}}')) {
            const titleString: string = `<span>Nok Persona</span>`
            currentHtml = currentHtml.replace('{{persona_title}}', titleString );
        }
        if (currentHtml.includes('{{persona_telephone}}')) {
            const phoneString: string = `<a href="tel:00000000001" 
                                    style="color:inherit;text-decoration:inherit;font-size:inherit;font-weight:inherit">
                                    00000 000001</a>`
            currentHtml = currentHtml.replace('{{persona_telephone}}', phoneString );
        }
        if (currentHtml.includes('{{nok_company_logo}}')) {
            const imgString: string = `<img src="${hostUrl}images/admin/nok/nn-logo-white.png" alt="${placeholderImages[1]}" style="width: 100%;height:auto;max-width: 100%;" />`;
            currentHtml = currentHtml.replace('{{nok_company_logo}}', imgString );
        }
        if (currentHtml.includes('{{nok_url}}')) {
            const urlString: string = `<a href="https://noknok.ai" 
                                    target="_blank"
                                    style="color:inherit;text-decoration:inherit;font-size:inherit;font-weight:inherit">
                                    https://noknok.ai</a>`
            currentHtml = currentHtml.replace('{{nok_url}}', urlString );
        }
        return currentHtml;
    }

    return (
        <>
            <Grid container spacing={2} sx={ { height: '400px', width: '100%' } }>
                <Grid size={6}>
                    <p>{ "You can use the field below to add inline styled HTML for the email signature." }</p>
                    <p>{ "Use the variables to inject various personnel and/or company data into your HTML." }</p>
                    <p>{ "The HTML is sanitized so script tags will be removed." }</p>
                    <p>{ "Note: Remember to account for Outlook and other oddities with email HTML." }</p>
                </Grid>
                <Grid size={6}>
                    <p>
                        { "{{persona_name}} - this will inject a <span> with the persona name " }
                    </p>
                    <p>
                        { "{{persona_image}} - this will inject an <img> with the persona image. The HQ image will be used if available to represent only. Production path used during email send " }
                    </p>
                    <p>
                        { "{{persona_email}} - this will inject an <a> with the persona email address" }
                    </p>
                    <p>
                        { "{{persona_title}} - this will inject a <span> with the persona job title" }
                    </p>
                    <p>
                        { "{{persona_telephone}} - this will inject a <a> with the persona phone number " }
                    </p>
                    <p>
                        { "{{nok_company_logo}} - this will inject an <img> with the Nok company logo " }
                    </p>
                    <p>
                        { "{{nok_url}} - this will inject a <a> with the Nok URL value " }
                    </p>

                </Grid>
                <Grid size={12}>
                    <Box sx={ {
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end'
                    } }>
                        <button type="button"
                                onClick={ handleShowPreviewClick }
                                style={ { cursor: 'pointer' } }
                                title="Show Preview">
                            <span>{ "Preview HTML" }</span>
                        </button>
                    </Box>
                </Grid>
            </Grid>
            <Box sx={ {
                width: '100%',
                border: '1px solid', borderColor: 'divider',
                display: 'flex',
                // flexDirection: 'column',
            } }>
                <Box sx={ {
                    width: '100%',
                    height: '500px',
                    overflowY: 'auto',
                    // display: 'flex',
                    // flexDirection: 'column',
                } }>
                    <Editor
                        defaultValue={ html }
                        height="100%"
                        defaultLanguage="html"
                        onChange={ setHtmlContent }
                    />

                </Box>
                <Drawer open={ showPreview } onClose={ handleShowPreviewClick } >
                    <Box sx={ {
                        width: '100%',
                        height: '100%',
                        background: '#fff',
                        padding: '10px'
                    } }>
                        <div style={ { width: '100%', maxWidth: '100%', display: 'flex' } } dangerouslySetInnerHTML={ { __html: previewHtml } }  />
                    </Box>
                </Drawer>

             </Box>


        </>

    );
}